import { Chart, LegendItem, ChartOptions, registerables } from "chart.js";
import ChartDataLabels, { Context } from "chartjs-plugin-datalabels";
import { Base } from "ui/Scripts/source/framework/base";
import { ChartValueFormatting } from "ui/Scripts/source/models/enums";
import { IReportChartDataSet } from "ui/Scripts/source/models/report/reportChartDataSet";
import { Translations } from "ui/Scripts/source/models/translations";

export const initializeChartJs = () => {
    Chart.register({
        id: "NoData",
        afterDraw: function (chart) {
            // you can use the previous examples "if" here instead if you want.
            // This not only checks the dataset lenght but also the data in each set.
            // If there is no data across all datasets, the message will display
            if (chart.data.datasets.map((d) => d.data.length).reduce((p, a) => p + (a ?? 0), 0) === 0) {
                const ctx = chart.ctx;
                const width = chart.width;
                const height = chart.height;
                chart.clear();
                ctx.save();
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                // you can specify a font if you want instead, this gets the font family from the body tag
                ctx.font = `1.5rem ${window.getComputedStyle(document.body).fontFamily}`;
                // you can omit this or use your own logic to adjust the font color.
                // `prefersDark` is a custom property I have
                ctx.fillStyle = "black";
                ctx.fillText(Translations.NoDataToShow, width / 2, height / 2);
                ctx.restore();
            }
        },
    },
    ...registerables);
    Chart.register(ChartDataLabels);
};

export const ChartColors = ["#a6cee3", "#1f78b4", "#b2df8a", "#33a02c", "#fb9a99", "#e31a1c", "#fdbf6f", "#ff7f00", "#cab2d6", "#6a3d9a", "#ffff99", "#b15928"];

export const handleLegendHover = (evt, legendItem: LegendItem, chart: Chart) => {
    const datasetIndex = legendItem.datasetIndex ?? 0;
    const index = legendItem.index ?? chart.data.datasets[datasetIndex].data.findIndex(i => i as number > 0);
    const activeElement = {
        datasetIndex,
        index: index > -1 ? index : 0
    };
    const chartArea = chart.chartArea;
    chart.tooltip.setActiveElements([activeElement],
        {
            x: (chartArea.left + chartArea.right) / 2,
            y: (chartArea.top + chartArea.bottom) / 2,
        });
    chart.update();
};

type ChartTitleOptions = NonNullable<ChartOptions["plugins"]>["title"];

export const getTitleOptions = (title: string): ChartTitleOptions => {
    return {
        display: !!title,
        align: "start",
        text: title,
        font: {
            family: "'Inter',sans-serif",
            size: 13
        }
    };
};

const getFormattedValue = (value: any, chartValueFormatting: ChartValueFormatting, defaultValue: any) => {
    if (chartValueFormatting === ChartValueFormatting.TimeSpan) {
        return Base.timeSpanToString(value as number);
    }
    //Integer
    if (chartValueFormatting === ChartValueFormatting.Integer && Base.isNumber(value) && !isNaN(value)) {
        const decimals = Base.getDecimalCountNum(Base.roundToDecimals(value, 1));
        return Base.roundToDecimals(value, 1).toLocaleFixed(decimals);
    }
    //Float
    if (value !== null && value !== undefined && Base.isNumber(value) && !isNaN(value)) {
        const decimals = Base.getDecimalCountNum(value);
        return value.toLocaleFixed(decimals);
    }
    return defaultValue;
};

export const getToolTipFormattedValue = (chartTooltipFormat: ChartValueFormatting, raw: unknown, formattedValue: string): string => {
    return getFormattedValue(raw, chartTooltipFormat, formattedValue);
};

export const getChartDataLabelFormatter = (value: any, context: Context, dataSets: IReportChartDataSet[]) => {
    const chartTooltipFormat = dataSets[context.datasetIndex].chartValueFormatting;
    return getFormattedValue(value, chartTooltipFormat, value);
};